<template>
  <div class="fixed inset-0 bg-gray-800 bg-opacity-50 flex items-center justify-center p-4 z-50 transition-opacity duration-300 animate-modalFade">
    <div class="w-full max-w-4xl bg-gray-800 text-gray-300 rounded-lg p-2 sm:p-5 flex flex-col gap-4 relative shadow-lg border border-gray-700 animate-scaleIn">
      <!-- Inventory Content -->
      <div class="flex flex-col gap-4">
        <!-- Toolbar: Simplified with Dropdowns for Actions -->
        <div class="block sm:flex justify-between items-center bg-gray-700 rounded-md p-1 border border-gray-700">
          <!-- Left Section: Weight, Upgrade, Sort/Filter Combined -->
          <div class="flex flex-wrap justify-center sm:justify-start items-center gap-2 mb-1 sm:mb-0">
            <button
              @click="showBagUpgradeModal = true"
              class="bg-indigo-600 hover:bg-indigo-700 text-white text-xs sm:text-sm py-1 px-3 rounded-md transition-colors duration-200 flex items-center gap-2 border border-indigo-700"
              :disabled="isLoading || isRestrictedInventoryEvent"
            >
              <i class="fas fa-plus text-xs"></i><span class="hidden sm:block">อัปเกรดกระเป๋า</span>
            </button>
            <i class="fas fa-weight-hanging text-indigo-400 text-xs animate-swing"></i>
            <div class="flex items-center space-x-2 text-xs sm:text-sm text-gray-300">
              <div class="hidden sm:block">น้ำหนัก:</div>
              <div class="px-2 py-0.5 rounded bg-gray-800 border border-gray-600 text-gray-100">
                {{ currentWeight }} / {{ maxLoad }}
              </div>
            </div>
           
            <select
              v-model="displayPreference"
              @change="updatePreferences"
              class="bg-gray-800 text-gray-300 rounded-md px-2 py-1 text-xs sm:text-sm focus:outline-none focus:ring-2 focus:ring-indigo-600 border border-gray-600"
            >
              <option value="type">ประเภท</option>
              <option value="inventory">ล่าสุด</option>
              <option value="locked">ล็อกแล้ว</option>
              <option value="unlocked">ไม่ล็อก</option>
            </select>
          </div>
          <!-- Right Section: Actions Dropdown + Decompose Button + Sell Button -->
          <div class="flex justify-center sm:justify-end items-center gap-2">
            <!-- Actions Dropdown -->
            <div class="relative">
              <button
                @click="toggleActionsDropdown"
                class="bg-gray-600 hover:bg-gray-500 text-white text-xs sm:text-sm py-1 px-3 rounded-md transition-colors duration-200 flex items-center gap-2 border border-gray-700"
                :disabled="isLoading || isRestrictedInventoryEvent"
              >
                <i class="fas fa-cog text-xs"></i><span class="hidden sm:block">จัดการ</span>
              </button>
              <transition name="dropdown">
                <div
                  v-if="showActionsDropdown"
                  class="absolute top-full left-0 mt-2 w-48 bg-gray-800 rounded-md shadow-lg z-10 border border-gray-700"
                >
                  <ul class="py-1">
                    <li v-if="showMoveToStorageButton">
                      <button
                        @click="showMoveToStorageModal = true; showActionsDropdown = false"
                        class="w-full text-left px-4 py-2 text-sm text-gray-300 hover:bg-gray-700 flex items-center gap-2"
                        :disabled="items.length === 0 || isRestrictedInventoryEvent"
                      >
                        <i class="fas fa-warehouse text-indigo-400 text-xs"></i> ย้ายไปคลัง
                      </button>
                    </li>
                    <li v-if="hasLockableItems">
                      <button
                        @click="showToggleLockModal = true; toggleLockMode = 'lock'; showActionsDropdown = false"
                        class="w-full text-left px-4 py-2 text-sm text-gray-300 hover:bg-gray-700 flex items-center gap-2"
                        :disabled="isRestrictedInventoryEvent"
                      >
                        <i class="fas fa-lock text-red-400 text-xs"></i> ล็อกทั้งหมด
                      </button>
                    </li>
                    <li v-if="hasLockedItems">
                      <button
                        @click="showToggleLockModal = true; toggleLockMode = 'unlock'; showActionsDropdown = false"
                        class="w-full text-left px-4 py-2 text-sm text-gray-300 hover:bg-gray-700 flex items-center gap-2"
                        :disabled="isRestrictedInventoryEvent"
                      >
                        <i class="fas fa-lock-open text-green-400 text-xs"></i> ปลดล็อกทั้งหมด
                      </button>
                    </li>
                  </ul>
                </div>
              </transition>
            </div>
            <!-- Decompose All Button -->
            <button
              v-if="hasDecomposableItems"
              @click="showDecomposeModal = true"
              class="bg-orange-600 hover:bg-orange-700 text-white text-xs sm:text-sm py-1 px-3 rounded-md transition-colors duration-200 flex items-center gap-2 border border-orange-700"
              :disabled="isLoading || isRestrictedInventoryEvent"
            >
              <i class="fas fa-tools text-xs"></i><span>แยกทั้งหมด</span>
            </button>
            <!-- Sell Button -->
            <button
              @click="showSellModal = true"
              class="bg-indigo-600 hover:bg-indigo-700 text-white text-xs sm:text-sm py-1 px-3 rounded-md transition-colors duration-200 flex items-center gap-2 border border-indigo-700"
              :disabled="isLoading || isRestrictedInventoryEvent"
            >
              <i class="fas fa-coins text-xs"></i><span>ขาย</span>
            </button>
          </div>
        </div>
        <!-- Messages -->
        <div v-if="warningMessage" class="text-yellow-400 text-xs sm:text-sm text-center">
          {{ warningMessage }}
        </div>
        <div v-if="error" class="text-red-300 text-xs sm:text-sm text-center">
          {{ error }}
        </div>
        <div v-if="isRestrictedInventoryEvent" class="text-red-400 text-xs sm:text-sm text-center font-semibold">
          <i class="fas fa-exclamation-triangle mr-1"></i>
          ตัวละครกำลังอยู่ในเหตุการณ์พิเศษ ไม่สามารถจัดการกระเป๋าได้
        </div>
        <!-- Loading Items -->
        <div v-if="loading" class="flex items-center justify-center py-6">
          <div class="animate-spin rounded-full h-12 w-12 border-t-4 border-red-600"></div>
        </div>
        <!-- Items -->
        <transition-group
          v-else-if="sortedItemsComputed.length > 0"
          name="item"
          tag="div"
          :class="[sortedItemsComputed.length <= 8 ? 'h-auto' : 'h-80']"
          class="grid grid-cols-1 gap-1 sm:gap-1.5 overflow-y-auto"
          id="style-1"
        >
          <div
            v-for="item in sortedItemsComputed"
            :key="item.instanceId"
            @click="selectItem(item)"
            class="bg-gray-800 rounded-md p-1.5 flex items-center border border-gray-700 transition-all duration-200 hover:border-indigo-600 hover:bg-gray-900 cursor-pointer relative"
            :class="{ 'border-2 border-indigo-600': selectedItem && selectedItem.instanceId === item.instanceId }"
          >
            <img
              :src="(gameItems[item.templateId] && gameItems[item.templateId].img) || defaultImg"
              alt="Item"
              class="w-9 h-9 rounded-md object-cover border-2"
              :class="[
                item.rarityColors?.borderColor || 'border-gray-600',
                item.rarityColors?.bgColor || 'bg-gray-800'
              ]"
              @error="handleImageError"
            />
            <div class="flex w-full justify-between items-center pl-2">
              <div class="flex items-center space-x-2 min-w-0">
                <div class="text-xxs w-auto bg-gray-700 bg-opacity-80 px-1 py-1 rounded font-bold flex items-center justify-center space-x-1 border border-gray-600">
                  <i class="fas fa-star text-yellow-400"></i>
                  <span class="truncate">{{ item.rarity }}</span>
                </div>
                <div
                  class="text-xs sm:text-sm text-center flex items-center gap-1 truncate"
                  :class="item.rarityColors?.textColor || 'text-gray-300'"
                >
                  {{ item.type === 'หนังสือ' ? `${bookTypeOrder[gameItems[item.templateId]?.name || item.name] || ''}. ` : '' }}{{ gameItems[item.templateId]?.name || item.name }}<span v-if="item.states?.level">+{{ item.states.level }}</span>
                </div>
              </div>
              <div class="flex items-center space-x-2">
                <i
                  v-if="item.transfer"
                  class="fa-solid fa-arrow-right-arrow-left text-green-400 text-xs"
                  title="โอนได้"
                  aria-label="โอนได้"
                ></i>
                <i v-if="item.decomposable && item.name !== 'กองขยะ'" class="fa-solid fa-tools text-green-400 text-xs"></i>
                <i v-if="item.states?.locked" class="fas fa-lock text-red-400 text-xs"></i>
                <i v-else class="fas fa-lock-open text-green-400 text-xs"></i>
              </div>
            </div>
          </div>
        </transition-group>
        <div v-else class="text-center text-gray-300 py-6">
          <p class="text-sm">ไม่มีไอเทมในกระเป๋า</p>
        </div>
        <!-- Modals -->
        <DetailItemModal
          v-if="showDetailModal"
          :item="selectedItem"
          :itemId="selectedItem.id"
          @close="showDetailModal = false"
        />
        <MoveToStorageModal
          v-if="showMoveToStorageModal"
          :items="unlockedItems"
          @close="showMoveToStorageModal = false"
          @items-moved="handleItemsMoved"
        />
        <DecomposeModal
          v-if="showDecomposeModal"
          :items="unlockedDecomposableItems"
          @close="showDecomposeModal = false"
          @items-decomposed="handleItemsDecomposed"
        />
        <ToggleLockModal
          v-if="showToggleLockModal"
          :items="items"
          :mode="toggleLockMode"
          @close="showToggleLockModal = false"
          @items-toggled="handleItemsToggled"
        />
        <SellModal
          v-if="showSellModal"
          :items="unlockedItems"
          :bannedItems="bannedItems"
          @close="showSellModal = false"
          @items-sold="handleItemsSold"
        />
    <KillerRobotModal
  v-if="showKillerRobotModal && selectedItem"
  :itemId="selectedItem.instanceId || selectedItem.id"
  :item="selectedItem"
  :states="selectedItem.states || {}"
  :inventoryItems="items"
  @close="showKillerRobotModal = false"
  @robot-updated="handleRobotUpdated"
/>
        <VehicleFuelModal
          v-if="showVehicleFuelModal"
          :itemId="selectedItem.id"
          :item="selectedItem"
          :inventoryItems="items"
          @close="showVehicleFuelModal = false"
          @vehicle-updated="handleVehicleUpdated"
        />
        <MechanicalUpgradeModal
          v-if="showMechanicalUpgradeModal"
          :itemId="selectedItem.id"
          :inventoryItems="items"
          @close="showMechanicalUpgradeModal = false"
          @mechanical-upgraded="handleMechanicalUpdated"
          @mechanical-reset="handleMechanicalUpdated"
        />
        <EquipmentUpgradeModal
          v-if="showEquipmentUpgradeModal"
          :itemId="selectedItem.id"
          :inventoryItems="items"
          @close="showEquipmentUpgradeModal = false"
          @equipment-upgraded="handleEquipmentUpdated"
        />
        <!-- BombModal removed -->
        <DisarmTrapModal
          v-if="showDisarmTrapModal"
          :show="showDisarmTrapModal"
          :item="selectedItem"
          @close="showDisarmTrapModal = false"
          @disarm-success="handleDisarmSuccess"
        />
        <InstallBombModal
          v-if="showInstallBombModal"
          :itemId="selectedItem.id"
          :item="selectedItem"
          @close="showInstallBombModal = false"
          @bomb-success="handleBombSuccess"
        />
        <LootModal
          v-if="showLootModal && selectedItem"
          :item="selectedItem"
          @close="showLootModal = false"
          @loot-success="handleLootSuccess"
        />
        <InstallCardModal
          v-if="showInstallCardModal"
          :itemId="selectedItem.id"
          :inventoryItems="items"
          @close="showInstallCardModal = false"
          @card-installed="handleCardInstalled"
        />
        <UninstallCardModal
          v-if="showUninstallCardModal"
          :itemId="selectedItem.id"
          :inventoryItems="items"
          @close="showUninstallCardModal = false"
          @card-uninstalled="handleCardUninstalled"
        />
        <ExorciseModal
          v-if="showExorciseModal"
          :itemId="selectedItem.id"
          :item="selectedItem"
          @close="showExorciseModal = false"
          @exorcise-success="handleExorciseSuccess"
        />
        <!-- Action Buttons -->
        <div v-if="selectedItem && !isLoading" class="flex justify-center gap-3 flex-wrap relative">
          <!-- Active event blocker -->
          <div
            v-if="isRestrictedInventoryEvent"
            class="absolute inset-0 bg-gray-900 bg-opacity-60 z-10 rounded-md flex items-center justify-center"
          >
            <span class="text-red-400 text-xs font-semibold text-center px-2">
              <i class="fas fa-exclamation-triangle mr-1"></i>
              ไม่สามารถจัดการไอเทมได้ขณะอยู่ในเหตุการณ์พิเศษ
            </span>
          </div>
          <!-- แผนที่ -->
          <button
            v-if="selectedItem.type === 'แผนที่' && !confirmReadMap"
            @click="setAction('confirmReadMap')"
            class="flex items-center gap-2 bg-purple-600 hover:bg-purple-700 text-white text-xs sm:text-sm py-2 px-4 rounded-md transition-colors duration-200 border border-purple-700"
          >
            <i class="fas fa-map text-xs"></i><span class="hidden sm:block">อ่านแผนที่</span>
          </button>
          <button
            v-if="selectedItem.type === 'แผนที่' && confirmReadMap"
            @click="readMap"
            class="bg-purple-600 hover:bg-purple-700 text-white text-xs sm:text-sm py-2 px-4 rounded-md transition-colors duration-200 flex items-center gap-2 border border-purple-700"
            :disabled="isLoading"
          >
            <span v-if="!isLoading"><i class="fas fa-check text-xs"></i> ยืนยันอ่านแผนที่</span>
            <div v-else class="animate-spin rounded-full h-4 w-4 border-t-2 border-white"></div>
          </button>
          <!-- อาหาร -->
          <button
            v-if="selectedItem.type === 'อาหาร' && selectedItem.name !== 'ซองบุหรี่' && !confirmConsume"
            @click="setAction('confirmConsume')"
            class="flex items-center gap-2 bg-yellow-600 hover:bg-yellow-700 text-white text-xs sm:text-sm py-2 px-4 rounded-md transition-colors duration-200 border border-yellow-700"
          >
            <i class="fas fa-utensils text-xs"></i><span class="hidden sm:block">กินอาหาร</span>
          </button>
          <button
            v-if="selectedItem.type === 'อาหาร' && selectedItem.name !== 'ซองบุหรี่' && confirmConsume"
            @click="consumeFood"
            class="bg-yellow-600 hover:bg-yellow-700 text-white text-xs sm:text-sm py-2 px-4 rounded-md transition-colors duration-200 flex items-center gap-2 border border-yellow-700"
            :disabled="isLoading"
          >
            <span v-if="!isLoading"><i class="fas fa-check text-xs"></i> ยืนยันกินอาหาร</span>
            <div v-else class="animate-spin rounded-full h-4 w-4 border-t-2 border-white"></div>
          </button>
          <!-- บุหรี่ -->
          <button
            v-if="selectedItem.type === 'อาหาร' && selectedItem.name === 'ซองบุหรี่' && !confirmSmoke"
            @click="setAction('confirmSmoke')"
            class="flex items-center gap-2 bg-gray-700 hover:bg-gray-600 text-white text-xs sm:text-sm py-2 px-4 rounded-md transition-colors duration-200 border border-gray-600"
          >
            <i class="fas fa-smoking text-xs"></i><span class="hidden sm:block">ดูดบุหรี่</span>
          </button>
          <button
            v-if="selectedItem.type === 'อาหาร' && selectedItem.name === 'ซองบุหรี่' && confirmSmoke"
            @click="smokeCigarette"
            class="bg-gray-700 hover:bg-gray-600 text-white text-xs sm:text-sm py-2 px-4 rounded-md transition-colors duration-200 flex items-center gap-2 border border-gray-600"
            :disabled="isLoading"
          >
            <span v-if="!isLoading"><i class="fas fa-check text-xs"></i> ยืนยันดูดบุหรี่</span>
            <div v-else class="animate-spin rounded-full h-4 w-4 border-t-2 border-white"></div>
          </button>
          <!-- หนังสือ -->
          <button
            v-if="selectedItem.type === 'หนังสือ' && !confirmLearn"
            @click="setAction('confirmLearn')"
            class="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white text-xs sm:text-sm py-2 px-4 rounded-md transition-colors duration-200 border border-blue-700"
          >
            <i class="fas fa-book-open text-xs"></i><span class="hidden sm:block">เรียนรู้ทักษะ</span>
          </button>
          <button
            v-if="selectedItem.type === 'หนังสือ' && confirmLearn"
            @click="learnSkill"
            class="bg-blue-600 hover:bg-blue-700 text-white text-xs sm:text-sm py-2 px-4 rounded-md transition-colors duration-200 flex items-center gap-2 border border-blue-700"
            :disabled="isLoading"
          >
            <span v-if="!isLoading"><i class="fas fa-check text-xs"></i> ยืนยันการเรียนรู้</span>
            <div v-else class="animate-spin rounded-full h-4 w-4 border-t-2 border-white"></div>
          </button>
          <!-- ใส่อุปกรณ์ทั่วไป -->
          <button
            v-if="isEquippable(selectedItem.type) && !confirmEquip"
            @click="setAction('confirmEquip')"
            class="flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white text-xs sm:text-sm py-2 px-4 rounded-md transition-colors duration-200 border border-green-700"
          >
            <i class="fas fa-tshirt text-xs"></i><span class="hidden sm:block">ใส่ไอเทม</span>
          </button>
          <button
            v-if="isEquippable(selectedItem.type) && confirmEquip"
            @click="equipItem"
            class="bg-green-600 hover:bg-green-700 text-white text-xs sm:text-sm py-2 px-4 rounded-md transition-colors duration-200 flex items-center gap-2 border border-green-700"
            :disabled="isLoading"
          >
            <span v-if="!isLoading"><i class="fas fa-check text-xs"></i> ยืนยันใส่ไอเทม</span>
            <div v-else class="animate-spin rounded-full h-4 w-4 border-t-2 border-white"></div>
          </button>
          <!-- ติดตั้งการ์ด -->
          <button
            v-if="isInstallableCard(selectedItem)"
            @click="showInstallCardModal = true"
            class="bg-indigo-600 hover:bg-indigo-700 text-white text-xs sm:text-sm py-2 px-4 rounded-md transition-colors duration-200 flex items-center gap-2 border border-indigo-700"
            :disabled="isLoading"
          >
            <span v-if="!isLoading"><i class="fas fa-id-card text-xs"></i> ติดตั้งการ์ด</span>
            <div v-else class="animate-spin rounded-full h-4 w-4 border-t-2 border-white"></div>
          </button>
          <!-- อวัยวะจักรกล -->
          <button
            v-if="selectedItem.type === 'อวัยวะจักรกล' && !confirmInstallMechanical"
            @click="setAction('confirmInstallMechanical')"
            class="flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white text-xs sm:text-sm py-2 px-4 rounded-md transition-colors duration-200 border border-green-700"
          >
            <i class="fas fa-cogs text-xs"></i><span class="hidden sm:block">ติดตั้งอวัยวะจักรกล</span>
          </button>
          <button
            v-if="selectedItem.type === 'อวัยวะจักรกล' && confirmInstallMechanical"
            @click="installMechanicalOrgan"
            class="bg-green-600 hover:bg-green-700 text-white text-xs sm:text-sm py-2 px-4 rounded-md transition-colors duration-200 flex items-center gap-2 border border-green-700"
            :disabled="isLoading"
          >
            <span v-if="!isLoading"><i class="fas fa-check text-xs"></i> ยืนยันติดตั้งอวัยวะจักรกล</span>
            <div v-else class="animate-spin rounded-full h-4 w-4 border-t-2 border-white"></div>
          </button>
          <button
            v-if="selectedItem.type === 'อวัยวะจักรกล'"
            @click="showMechanicalUpgradeModal = true"
            class="flex items-center gap-2 bg-purple-600 hover:bg-purple-700 text-white text-xs sm:text-sm py-2 px-4 rounded-md transition-colors duration-200 border border-purple-700"
          >
            <i class="fas fa-arrow-up text-xs"></i><span class="hidden sm:block">อัปเกรดอวัยวะจักรกล</span>
          </button>
          <button
            v-if="selectedItem.type === 'อวัยวะจักรกล' && hasInstalledCards(selectedItem)"
            @click="showUninstallCardModal = true"
            class="bg-red-600 hover:bg-red-700 text-white text-xs sm:text-sm py-2 px-4 rounded-md transition-colors duration-200 flex items-center gap-2 border border-red-700"
            :disabled="isLoading"
          >
            <i class="fas fa-id-card text-xs"></i>
            <span>ถอนการติดตั้งการ์ด</span>
          </button>
          <!-- อัปเกรดอุปกรณ์ -->
          <button
            v-if="isUpgradableEquipment(selectedItem.type) && selectedItem.rarity >= 7"
            @click="showEquipmentUpgradeModal = true"
            class="flex items-center gap-2 bg-purple-600 hover:bg-purple-700 text-white text-xs sm:text-sm py-2 px-4 rounded-md transition-colors duration-200 border border-purple-700"
          >
            <i class="fas fa-arrow-up text-xs"></i><span class="hidden sm:block">อัปเกรดไอเทม</span>
          </button>
          <!-- พาหนะ -->
          <button
            v-if="selectedItem.type === 'พาหนะ' && !confirmUseVehicle"
            @click="setAction('confirmUseVehicle')"
            class="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white text-xs sm:text-sm py-2 px-4 rounded-md transition-colors duration-200 border border-blue-700"
          >
            <i class="fas fa-motorcycle text-xs"></i><span class="hidden sm:block">ใช้งาน{{ selectedItem.name }}</span>
          </button>
          <button
            v-if="selectedItem.type === 'พาหนะ' && confirmUseVehicle"
            @click="useVehicle"
            class="bg-blue-600 hover:bg-blue-700 text-white text-xs sm:text-sm py-2 px-4 rounded-md transition-colors duration-200 flex items-center gap-2 border border-blue-700"
            :disabled="isLoading"
          >
            <span v-if="!isLoading"><i class="fas fa-check text-xs"></i> ยืนยันใช้งาน{{ selectedItem.name }}</span>
            <div v-else class="animate-spin rounded-full h-4 w-4 border-t-2 border-white"></div>
          </button>
          <button
            v-if="selectedItem.type === 'พาหนะ'"
            @click="showVehicleFuelModal = true"
            class="flex items-center gap-2 bg-yellow-600 hover:bg-yellow-700 text-white text-xs sm:text-sm py-2 px-4 rounded-md transition-colors duration-200 border border-yellow-700"
          >
            <i class="fas fa-gas-pump text-xs"></i><span class="hidden sm:block">เติมน้ำมัน</span>
          </button>
          <!-- หุ่นยนต์ / ระเบิด / ระเบิด / ระเบิด / ปลดกัปดัก / ปล้น -->
          <button
            v-if="isKillerRobotItem(selectedItem)"
            @click="useRobot"
            class="flex items-center gap-2 bg-purple-600 hover:bg-purple-700 text-white text-xs sm:text-sm py-2 px-4 rounded-md transition-colors duration-200 border border-purple-700"
          >
            <i class="fas fa-robot text-xs"></i><span class="hidden sm:block">ใช้งานหุ่นยนต์</span>
          </button>
          <button
            v-if="selectedItem.type === 'ระเบิด'"
            @click="showInstallBombModal = true"
            class="flex items-center gap-2 bg-red-600 hover:bg-red-700 text-white text-xs sm:text-sm py-2 px-4 rounded-md transition-colors duration-200 border border-red-700"
          >
            <i class="fas fa-bomb text-xs"></i><span class="hidden sm:block">ติดตั้งระเบิด</span>
          </button>
          <button
            v-if="selectedItem.type === 'ปลดกัปดัก'"
            @click="showDisarmTrapModal = true"
            class="flex items-center gap-2 bg-purple-600 hover:bg-purple-700 text-white text-xs sm:text-sm py-2 px-4 rounded-md transition-colors duration-200 border border-purple-700"
          >
            <i class="fas fa-explosion text-xs"></i><span class="hidden sm:block">ปลดกัปดัก</span>
          </button>
          <button
            v-if="selectedItem.type === 'ปล้น'"
            @click="showLootModal = true"
            class="flex items-center gap-2 bg-red-600 hover:bg-red-700 text-white text-xs sm:text-sm py-2 px-4 rounded-md transition-colors duration-200 border border-red-700"
          >
            <i class="fas fa-sack-dollar text-xs"></i><span class="hidden sm:block">ปล้น</span>
          </button>
          <!-- วิญญาณ -->
          <button
            v-if="selectedItem.type === 'วิญญาณ' && !confirmSummon"
            @click="setAction('confirmSummon')"
            class="flex items-center gap-2 bg-purple-600 hover:bg-purple-700 text-white text-xs sm:text-sm py-2 px-4 rounded-md transition-colors duration-200 border border-purple-700"
          >
            <i class="fas fa-ghost text-xs"></i><span class="hidden sm:block">อัญเชิญวิญญาณ</span>
          </button>
          <button
            v-if="selectedItem.type === 'วิญญาณ' && confirmSummon"
            @click="summonGhost"
            class="bg-purple-600 hover:bg-purple-700 text-white text-xs sm:text-sm py-2 px-4 rounded-md transition-colors duration-200 flex items-center gap-2 border border-purple-700"
            :disabled="isLoading"
          >
            <span v-if="!isLoading"><i class="fas fa-check text-xs"></i> ยืนยันการอัญเชิญ</span>
            <div v-else class="animate-spin rounded-full h-4 w-4 border-t-2 border-white"></div>
          </button>
          <!-- ไล่ผี -->
          <button
            v-if="selectedItem.type === 'ไล่ผี'"
            @click="showExorciseModal = true"
            class="bg-purple-600 hover:bg-purple-700 text-white text-xs sm:text-sm py-2 px-4 rounded-md transition-colors duration-200 flex items-center gap-2 border border-purple-700"
          >
            <i class="fas fa-ghost text-xs"></i><span>ไล่ผี</span>
          </button>
          <!-- โอน / รักษา / แยกชิ้น -->
          <button
            v-if="selectedItem.transfer && !confirmTransfer"
            @click="setAction('confirmTransfer')"
            class="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white text-xs sm:text-sm py-2 px-4 rounded-md transition-colors duration-200 border border-blue-700"
          >
            <i class="fa-solid fa-arrow-right-arrow-left text-xs"></i><span class="hidden sm:block">ส่งไอเทม</span>
          </button>
          <div v-if="selectedItem.transfer && confirmTransfer" class="flex items-center gap-2">
            <input
              v-model="recipientUid"
              type="text"
              placeholder="กรอก UID ผู้รับ"
              class="bg-gray-800 text-gray-300 rounded-md px-3 py-1 text-xs sm:text-sm focus:outline-none focus:ring-2 focus:ring-indigo-600 border border-gray-600"
            />
            <button
              @click="transferItem"
              class="bg-blue-600 hover:bg-blue-700 text-white text-xs sm:text-sm py-2 px-4 rounded-md transition-colors duration-200 flex items-center gap-2 border border-blue-700"
            >
              <i class="fas fa-check text-xs"></i><span>ยืนยันส่งไอเทม</span>
            </button>
          </div>
          <button
            v-if="selectedItem.type === 'รักษา' && !confirmHeal"
            @click="setAction('confirmHeal')"
            class="flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white text-xs sm:text-sm py-2 px-4 rounded-md transition-colors duration-200 border border-green-700"
            :disabled="hasFullHP"
          >
            <i class="fas fa-heartbeat text-xs"></i><span class="hidden sm:block">รักษา</span>
          </button>
          <button
            v-if="selectedItem.type === 'รักษา' && confirmHeal"
            @click="heal"
            class="bg-green-600 hover:bg-green-700 text-white text-xs sm:text-sm py-2 px-4 rounded-md transition-colors duration-200 flex items-center gap-2 border border-green-700"
            :disabled="isLoading || hasFullHP"
          >
            <i class="fas fa-check text-xs"></i><span>ยืนยันการรักษา</span>
          </button>
          <button
            v-if="selectedItem.decomposable && !selectedItem.states?.locked && selectedItem.name !== 'กองขยะ' && !confirmDecompose"
            @click="setAction('confirmDecompose')"
            class="flex items-center gap-2 bg-orange-600 hover:bg-orange-700 text-white text-xs sm:text-sm py-2 px-4 rounded-md transition-colors duration-200 border border-orange-700"
          >
            <i class="fas fa-tools text-xs"></i><span class="hidden sm:block">แยกชิ้นส่วน</span>
          </button>
          <button
            v-if="selectedItem.decomposable && !selectedItem.states?.locked && selectedItem.name !== 'กองขยะ' && confirmDecompose"
            @click="decomposeItem"
            class="bg-orange-600 hover:bg-orange-700 text-white text-xs sm:text-sm py-2 px-4 rounded-md transition-colors duration-200 flex items-center gap-2 border border-orange-700"
            :disabled="isLoading"
          >
            <i class="fas fa-check text-xs"></i><span>ยืนยันแยกชิ้นส่วน</span>
          </button>
          <!-- ใช้กับดัก -->
          <button
            v-if="selectedItem.type === 'กัปดัก'"
            @click="showUseTrapModal = true"
            class="flex items-center gap-2 bg-purple-600 hover:bg-purple-700 text-white text-xs sm:text-sm py-2 px-4 rounded-md transition-colors duration-200 border border-purple-700"
          >
            <i class="fas fa-road-spikes text-xs"></i><span class="hidden sm:block">ใช้งาน{{ selectedItem.name }}</span>
          </button>
          <!-- คลังเดี่ยว -->
          <button
            v-if="!confirmMoveToStorage"
            @click="setAction('confirmMoveToStorage')"
            class="flex items-center gap-2 bg-indigo-600 hover:bg-indigo-700 text-white text-xs sm:text-sm py-2 px-4 rounded-md transition-colors duration-200 border border-indigo-700"
          >
            <i class="fas fa-warehouse text-xs"></i><span class="hidden sm:block">เก็บเข้าคลัง</span>
          </button>
          <button
            v-if="confirmMoveToStorage"
            @click="moveToStorage"
            class="bg-indigo-600 hover:bg-indigo-700 text-white text-xs sm:text-sm py-2 px-4 rounded-md transition-colors duration-200 flex items-center gap-2 border border-indigo-700"
            :disabled="isLoading"
          >
            <i class="fas fa-check text-xs"></i><span>ยืนยันเก็บเข้าคลัง</span>
          </button>
          <!-- ข้อมูล / ล็อก / ทิ้ง -->
          <button
            @click="openDetailModal(selectedItem)"
            class="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white text-xs sm:text-sm py-2 px-4 rounded-md transition-colors duration-200 border border-blue-700"
          >
            <i class="fas fa-info-circle text-xs"></i><span class="hidden sm:block">ดูข้อมูลไอเทม</span>
          </button>
          <button
            v-if="!selectedItem.states?.locked && !confirmLock"
            @click="setAction('confirmLock')"
            class="flex items-center gap-2 bg-orange-600 hover:bg-orange-700 text-white text-xs sm:text-sm py-2 px-4 rounded-md transition-colors duration-200 border border-orange-700"
          >
            <i class="fas fa-lock text-xs"></i><span class="hidden sm:block">ล็อกไอเทม</span>
          </button>
          <button
            v-if="!selectedItem.states?.locked && confirmLock"
            @click="toggleLock(true)"
            class="bg-orange-600 hover:bg-orange-700 text-white text-xs sm:text-sm py-2 px-4 rounded-md transition-colors duration-200 flex items-center gap-2 border border-orange-700"
            :disabled="isLoading"
          >
            <i class="fas fa-check text-xs"></i><span>ยืนยันล็อกไอเทม</span>
          </button>
          <button
            v-if="selectedItem.states?.locked && !confirmUnlock"
            @click="setAction('confirmUnlock')"
            class="flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white text-xs sm:text-sm py-2 px-4 rounded-md transition-colors duration-200 border border-green-700"
          >
            <i class="fas fa-lock-open text-xs"></i><span class="hidden sm:block">ปลดล็อกไอเทม</span>
          </button>
          <button
            v-if="selectedItem.states?.locked && confirmUnlock"
            @click="toggleLock(false)"
            class="bg-green-600 hover:bg-green-700 text-white text-xs sm:text-sm py-2 px-4 rounded-md transition-colors duration-200 flex items-center gap-2 border border-green-700"
            :disabled="isLoading"
          >
            <i class="fas fa-check text-xs"></i><span>ยืนยันปลดล็อกไอเทม</span>
          </button>
          <button
            v-if="!confirmDiscard && !selectedItem.states?.locked"
            @click="setAction('confirmDiscard')"
            class="flex items-center gap-2 bg-red-600 hover:bg-red-700 text-white text-xs sm:text-sm py-2 px-4 rounded-md transition-colors duration-200 border border-red-700"
          >
            <i class="fas fa-trash text-xs"></i><span class="hidden sm:block">ทิ้งไอเทม</span>
          </button>
          <button
            v-if="confirmDiscard && !selectedItem.locked"
            @click="discardItem"
            class="bg-red-600 hover:bg-red-700 text-white text-xs sm:text-sm py-2 px-4 rounded-md transition-colors duration-200 flex items-center gap-2 border border-red-700"
            :disabled="isLoading"
          >
            <i class="fas fa-check text-xs"></i><span>ยืนยันทิ้งไอเทม</span>
          </button>
        </div>
      </div>
      <!-- Bag Upgrade Modal -->
      <transition name="modal">
        <div v-if="showBagUpgradeModal" class="fixed inset-0 bg-gray-900 bg-opacity-70 flex items-center justify-center z-50 animate-modalFade">
          <div class="bg-gray-800 p-6 rounded-lg border border-gray-700 shadow-lg w-full max-w-md animate-scaleIn">
            <div class="flex items-center gap-2 mb-4">
              <i class="fas fa-suitcase text-gray-100 text-2xl"></i>
              <h3 class="text-lg font-semibold text-gray-100">อัปเกรดกระเป๋า</h3>
            </div>
            <p class="text-sm text-gray-400 mb-4">เพิ่มขนาดกระเป๋าเพื่อพกของได้มากขึ้น</p>
            <div class="bg-gray-800 border border-gray-700 p-3 rounded-lg mb-3">
              <p class="text-sm text-gray-300 flex items-center gap-2">
                <i class="fas fa-weight-hanging text-indigo-400 text-sm"></i> ขนาดปัจจุบัน: {{ maxLoad }} / 200
              </p>
            </div>
            <div class="bg-gray-800 border border-gray-700 p-3 rounded-lg mb-3">
              <p class="text-sm text-gray-300 flex items-center gap-2">
                <i class="fas fa-money-bill text-green-300 text-sm"></i> ค่าใช้จ่าย: {{ nextUpgradeCost }}
              </p>
              <p class="text-sm text-gray-400">ผลลัพธ์: +10 หน่วยให้ขนาดกระเป๋า</p>
            </div>
            <div v-if="maxLoad >= 200" class="text-yellow-300 mb-3 flex items-center gap-2 text-sm">
              <i class="fas fa-exclamation-triangle text-yellow-300 text-sm"></i> ถึงขีดจำกัดสูงสุดแล้ว!
            </div>
            <div v-else-if="cash < nextUpgradeCost" class="text-red-300 mb-3 flex items-center gap-2 text-sm">
              <i class="fas fa-exclamation-circle text-red-300 text-sm"></i> เงินไม่เพียงพอ!
            </div>
            <div v-if="error" class="text-red-300 mb-3 text-sm">{{ error }}</div>
            <div class="flex justify-end gap-2">
              <button
                @click="upgradeBag"
                class="py-2 px-4 bg-indigo-600 hover:bg-indigo-700 border border-indigo-700 text-white rounded-md transition-all duration-200 flex items-center gap-2"
                :class="{ 'opacity-60 cursor-not-allowed': isUpgrading || maxLoad >= 200 || cash < nextUpgradeCost || isRestrictedInventoryEvent }"
                :disabled="isUpgrading || maxLoad >= 200 || cash < nextUpgradeCost || isRestrictedInventoryEvent"
              >
                <span v-if="!isUpgrading"><i class="fas fa-arrow-up text-xs"></i> อัปเกรด</span>
                <i v-else class="fas fa-spinner animate-spin text-indigo-200 text-sm"></i>
              </button>
              <button
                @click="showBagUpgradeModal = false"
                class="py-2 px-4 bg-gray-700 hover:bg-gray-600 border border-gray-600 text-white rounded-md transition-all duration-200 flex items-center gap-2"
              >
                <i class="fas fa-times text-xs"></i> ยกเลิก
              </button>
            </div>
          </div>
        </div>
      </transition>
      <!-- Use Trap Confirmation Modal -->
      <transition name="modal">
        <div v-if="showUseTrapModal" class="fixed inset-0 z-50 flex items-center justify-center bg-gray-900 bg-opacity-70 animate-modalFade">
          <div class="bg-gray-800 rounded-lg w-full max-w-md shadow-lg border border-gray-700 animate-scaleIn">
            <div class="p-4 border-b border-gray-700 flex justify-between items-center">
              <h1 class="text-lg font-semibold text-gray-100">ยืนยันการใช้งานกับดัก</h1>
              <button @click="showUseTrapModal = false" class="text-gray-300 hover:text-indigo-300 group">
                <i class="fas fa-times text-base group-hover:scale-110 transition-transform duration-200"></i>
              </button>
            </div>
            <div class="p-4 space-y-4">
              <h2 class="text-sm font-semibold text-gray-200">คำแนะนำก่อนใช้งานกับดัก</h2>
              <p class="text-yellow-300 text-sm">
                ควรใช้กับดักเมื่อคุณมีเพชรอย่างน้อย 100 ชิ้น เพราะหากมีเพชรน้อย ผู้เล่นอื่นอาจไม่สนใจโจมตีคุณ นี่เป็นเพียงคำแนะนำ คุณยังอาจถูกโจมตีแม้มีเพชรน้อยก็ได้
              </p>
              <ul class="list-disc list-inside text-gray-300 text-sm space-y-2">
                <li>เมื่อติดตั้งกับดักแล้ว คุณจะเข้าสู่เหตุการณ์ "ติดตั้งกับดัก" ซึ่งจะทำให้คุณไม่สามารถขายของหรือออกสำรวจได้ โปรดคิดให้ดีก่อนติดตั้ง</li>
              </ul>
            </div>
            <div class="p-4 border-t border-gray-700 flex justify-end gap-3">
              <button
                @click="useTrap"
                class="flex items-center bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-md font-semibold transition-all duration-200 border border-purple-700 text-sm group"
                :class="{ 'opacity-60 cursor-not-allowed': isLoading || isRestrictedInventoryEvent }"
                :disabled="isLoading || isRestrictedInventoryEvent"
              >
                <span v-if="!isLoading && !isRestrictedInventoryEvent"><i class="fas fa-check mr-1 text-sm group-hover:scale-110"></i> ยืนยันใช้งาน</span>
                <span v-else-if="isRestrictedInventoryEvent"><i class="fas fa-exclamation-triangle mr-1 text-sm"></i> อยู่ในเหตุการณ์พิเศษ</span>
                <i v-else class="fas fa-spinner fa-spin-pulse text-sm"></i>
              </button>
              <button
                @click="showUseTrapModal = false"
                class="flex items-center bg-gray-700 hover:bg-gray-600 text-white px-4 py-2 rounded-md font-semibold transition-all duration-200 border border-gray-600 text-sm group"
              >
                <i class="fas fa-times mr-1 text-sm group-hover:scale-110"></i> ยกเลิก
              </button>
            </div>
          </div>
        </div>
      </transition>
      <!-- Close Button -->
      <div v-if="!isLoading" class="flex justify-end">
        <button
          @click="$emit('closeInventory')"
          class="bg-gray-700 hover:bg-gray-600 text-white px-4 py-2 w-full rounded-md font-semibold transition-all duration-200 border border-gray-600 text-sm group"
        >
          <i class="fas fa-times mr-1 text-sm group-hover:scale-110"></i> ปิด
        </button>
      </div>
      <!-- Loading Indicator -->
      <div v-if="isLoading" class="flex items-center justify-center">
        <div class="animate-spin rounded-full h-12 w-12 border-t-4 border-red-600"></div>
      </div>
    </div>
  </div>
</template>
<script>
import { ref, computed, onMounted, onUnmounted, nextTick, watch } from 'vue';
import { projectFirestore, projectFunctions } from '@/firebase/config';
import getUser from '@/composables/getUser';
import { usePlayerStore } from '@/composables/usePlayerStore';
import { useInventoryStore } from '@/composables/useInventoryStore';
import { isOldSchema, triggerReMigration } from '@/composables/useMigrationGuard';
import DetailItemModal from '@/components/DetailItemModal.vue';
import MoveToStorageModal from '@/components/character/MoveToStorageModal.vue';
import DecomposeModal from '@/components/character/DecomposeModal.vue';
import ToggleLockModal from '@/components/character/ToggleLockModal.vue';
import SellModal from '@/components/character/SellModal.vue';
import KillerRobotModal from '@/components/KillerRobotModal.vue';
import VehicleFuelModal from '@/components/VehicleFuelModal.vue';
import MechanicalUpgradeModal from '@/components/MechanicalUpgradeModal.vue';
import EquipmentUpgradeModal from '@/components/EquipmentUpgradeModal.vue';
import DisarmTrapModal from '@/components/DisarmTrapModal.vue';
import InstallBombModal from '@/components/InstallBombModal.vue';
import LootModal from '@/components/character/LootModal.vue';
import InstallCardModal from '@/components/InstallCardModal.vue';
import UninstallCardModal from '@/components/UninstallCardModal.vue';
import ExorciseModal from '@/components/ExorciseModal.vue'; // Assuming you create this component
import { books } from '@/constants/books';
import gameItems from '@/data/gameItems.json';
import defaultImg from '@/assets/default-item.png';

export default {
  name: 'BagModal',
  emits: ['closeInventory'],
  components: {
    DetailItemModal,
    MoveToStorageModal,
    DecomposeModal,
    ToggleLockModal,
    SellModal,
    KillerRobotModal,
    VehicleFuelModal,
    MechanicalUpgradeModal,
    EquipmentUpgradeModal,
    DisarmTrapModal,
    InstallBombModal,
    LootModal,
    InstallCardModal,
    UninstallCardModal,
    ExorciseModal
  },
  setup(props, { emit }) {
    const { user } = getUser();
    const items = ref([]);
    const itemOrder = ref([]);
    const currentWeight = ref(0);
    const maxLoad = ref(20);
    const cash = ref(null);
    const loading = ref(true);
    const error = ref(null);
    const warningMessage = ref('');
    const selectedItem = ref(null);
    const confirmDiscard = ref(false);
    const confirmEquip = ref(false);
    const confirmLearn = ref(false);
    const confirmConsume = ref(false);
    const confirmReadMap = ref(false);
    const confirmTransfer = ref(false);
    const confirmDecompose = ref(false);
    const confirmHeal = ref(false);
    const confirmSmoke = ref(false);
    const confirmMoveToStorage = ref(false);
    const confirmUseVehicle = ref(false);
    const confirmLock = ref(false);
    const confirmUnlock = ref(false);
    const confirmInstallMechanical = ref(false);
    const confirmSummon = ref(false);
    const showBagUpgradeModal = ref(false);
    const showMoveToStorageModal = ref(false);
    const showDecomposeModal = ref(false);
    const showToggleLockModal = ref(false);
    const toggleLockMode = ref('lock');
    const showSellModal = ref(false);
    const showKillerRobotModal = ref(false);
    const showVehicleFuelModal = ref(false);
    const showMechanicalUpgradeModal = ref(false);
    const showEquipmentUpgradeModal = ref(false);
    const showUseTrapModal = ref(false);
    const showDisarmTrapModal = ref(false);
    const showInstallBombModal = ref(false);
    const showLootModal = ref(false);
    const showInstallCardModal = ref(false);
    const showUninstallCardModal = ref(false);
    const showExorciseModal = ref(false);
    const showActionsDropdown = ref(false);
    const showMoveToStorageButton = ref(true);
    const isUpgrading = ref(false);
    const recipientUid = ref('');
    const isLoading = ref(false);
    const showDetailModal = ref(false);
    const displayPreference = ref('type');
    const sortPreference = ref('type');
    const filterLocked = ref('all');
    const bannedItems = ref([]);
    const playerStore = usePlayerStore();
    const inventoryStore = useInventoryStore();
    let bannedUnsubscribe = null;
    const defaultSortOrder = [
      'พาหนะ', 'รักษา', 'กัปดัก', 'อาหาร', 'แผนที่','หุ่นยนต์สังหาร', 'ปลดกัปดัก','ระเบิด','ปล้น', 'การ์ด', 'หนังสือ', 'อวัยวะจักรกล', 'วิญญาณ' , 'ไล่ผี', 'หมวก', 'แว่นตา', 'เสื้อ', 'เสื้อคลุม', 'เกราะ', 'ปลอกแขน', 'ถุงมือ', 'กางเกง',
      'สนับแข้ง', 'รองเท้า', 'เข็มขัด', 'สร้อยคอ', 'แหวน', 'อาวุธ', 'อาวุธสำรอง',
      'ตะกรุด', 'ผ้ายันต์', 'สายสิญจน์', 'วัสดุวิญญาณ','วัสดุ','กล่อง','ทั่วไป'
    ];
    const sortOrder = ref([...defaultSortOrder]);
    const UPGRADE_COSTS = [
      20, 30, 40, 50, 50, 50, 50, 50, 50, 50,
      60, 70, 80, 90, 100, 100, 100, 100
    ];
    const bookTypeOrder = {};
    const typeCounts = {};
    const KILLER_ROBOT_TYPE = 'หุ่นยนต์สังหาร';
    books.forEach(book => {
      if (!typeCounts[book.type]) typeCounts[book.type] = 0;
      typeCounts[book.type]++;
      bookTypeOrder[book.name] = typeCounts[book.type];
    });
    const nextUpgradeCost = computed(() => {
      const currentUpgradeLevel = Math.floor((maxLoad.value - 20) / 10);
      if (currentUpgradeLevel >= UPGRADE_COSTS.length) return 0;
      return UPGRADE_COSTS[currentUpgradeLevel] || 0;
    });
    const hasFullHP = computed(() => {
      return false; // Placeholder, adjust based on actual HP logic
    });
    const eventData = ref(null);
    let eventUnsubscribe = null;
    const RESTRICTED_INVENTORY_EVENT_TYPES = [
      'fightZombie',
      'exploreSupernatural',
      'killerRobot',
      'attackRoomGuardianGhost',
      'waiting_loot',
      'ghostPvp'
    ];
    const isRestrictedInventoryEvent = computed(() => {
      return !eventData.value?.finished && RESTRICTED_INVENTORY_EVENT_TYPES.includes(eventData.value?.type);
    });
    const hasDecomposableItems = computed(() => {
      return items.value.some(item => item.decomposable && !item.states?.locked && item.name !== 'กองขยะ');
    });
    const hasLockableItems = computed(() => {
      return items.value.some(item => !item.states?.locked);
    });
    const hasLockedItems = computed(() => {
      return items.value.some(item => item.states?.locked);
    });
    const hasSellableItems = computed(() => {
      return items.value.some(item => item.type !== 'พาหนะ' && !item.locked && !bannedItems.value.includes(item.name));
    });
    const unlockedItems = computed(() => {
      return items.value.filter(item => !item.states?.locked);
    });
    const unlockedDecomposableItems = computed(() => {
      return items.value.filter(item => item.decomposable && !item.states?.locked && item.name !== 'กองขยะ');
    });
    const getItemOrderIndex = (item) => {
      const index = item.orderIndex ?? itemOrder.value.indexOf(item.instanceId);
      return index === -1 ? Number.MAX_SAFE_INTEGER : index;
    };
    const getTemplate = (item) => gameItems[item?.templateId] || {};
    const isKillerRobotItem = (item) => {
      const template = getTemplate(item);
      return item?.type === KILLER_ROBOT_TYPE || item?.states?.type === KILLER_ROBOT_TYPE || template.type === KILLER_ROBOT_TYPE;
    };
    const sortedItemsComputed = computed(() => {
      let itemsList = [...items.value];
      if (filterLocked.value === 'locked') {
        itemsList = itemsList.filter(item => item.locked);
      } else if (filterLocked.value === 'unlocked') {
        itemsList = itemsList.filter(item => !item.locked);
      }
      if (sortPreference.value === 'inventory') {
        itemsList.sort((a, b) => getItemOrderIndex(a) - getItemOrderIndex(b));
      } else if (sortPreference.value === 'type') {
        const typeOrder = sortOrder.value.reduce((acc, type, index) => {
          acc[type] = index + 1;
          return acc;
        }, {});
        itemsList.sort((a, b) => {
          const orderA = typeOrder[a.type] || 999;
          const orderB = typeOrder[b.type] || 999;
          if (orderA !== orderB) {
            return orderA - orderB;
          }
          const rarityA = a.rarity ? String(a.rarity) : '';
          const rarityB = b.rarity ? String(b.rarity) : '';
          if (rarityA !== rarityB) {
            return rarityA.localeCompare(rarityB, 'th');
          }
          const nameCompare = a.name.localeCompare(b.name, 'th');
          return nameCompare !== 0 ? nameCompare : getItemOrderIndex(a) - getItemOrderIndex(b);
        });
      }
      return itemsList;
    });
    const isEquippable = (type) => {
      const equippableTypes = [
        'หมวก', 'แว่นตา', 'เสื้อ', 'เสื้อคลุม', 'เกราะ', 'ปลอกแขน', 'ถุงมือ', 'กางเกง',
        'สนับแข้ง', 'รองเท้า', 'เข็มขัด', 'สร้อยคอ', 'แหวน', 'อาวุธ', 'อาวุธสำรอง',
        'ตะกรุด', 'ผ้ายันต์', 'สายสิญจน์'
      ];
      return equippableTypes.includes(type);
    };
    const isInstallableCard = (item) => {
      return item?.type === 'การ์ด' && !!item.compatibleEquipment;
    };
    const hasInstalledCards = (item) => {
      return Array.isArray(item?.cardSlots)
        ? item.cardSlots.length > 0
        : (item?.cardIds?.length || 0) > 0;
    };
    const isUpgradableEquipment = (type) => {
      const upgradableTypes = [
        'หมวก', 'แว่นตา', 'เสื้อ', 'เสื้อคลุม', 'เกราะ', 'ปลอกแขน', 'ถุงมือ', 'กางเกง',
        'สนับแข้ง', 'รองเท้า', 'เข็มขัด', 'สร้อยคอ', 'แหวน', 'อาวุธ', 'อาวุธสำรอง',
        'ตะกรุด', 'ผ้ายันต์', 'สายสิญจน์'
      ];
      return upgradableTypes.includes(type);
    };
    const updatePreferences = () => {
      switch (displayPreference.value) {
        case 'type':
          sortPreference.value = 'type';
          filterLocked.value = 'all';
          break;
        case 'inventory':
          sortPreference.value = 'inventory';
          filterLocked.value = 'all';
          break;
        case 'locked':
          sortPreference.value = 'type';
          filterLocked.value = 'locked';
          break;
        case 'unlocked':
          sortPreference.value = 'type';
          filterLocked.value = 'unlocked';
          break;
      }
      saveDisplayPreference();
    };
    const loadDisplayPreference = () => {
      const savedPreference = localStorage.getItem('inventoryDisplayPreference');
      if (savedPreference && ['type', 'inventory', 'locked', 'unlocked'].includes(savedPreference)) {
        displayPreference.value = savedPreference;
        updatePreferences();
      }
    };
    const saveDisplayPreference = () => {
      localStorage.setItem('inventoryDisplayPreference', displayPreference.value);
    };
    const loadSortOrder = () => {
      const savedOrder = localStorage.getItem('sortOrder');
      if (savedOrder) {
        const parsedOrder = JSON.parse(savedOrder);
        if (parsedOrder.length === defaultSortOrder.length && defaultSortOrder.every(type => parsedOrder.includes(type))) {
          sortOrder.value = parsedOrder;
        }
      }
    };
    const handleImageError = (event) => {
      event.target.src = defaultImg;
    };
    const processBagData = async (bagData) => {
      if (!bagData) {
        items.value = [];
        itemOrder.value = [];
        selectedItem.value = null;
        currentWeight.value = 0;
        maxLoad.value = 20;
        loading.value = false;
        showMoveToStorageButton.value = false;
        return;
      }
      if (isOldSchema(bagData)) {
        console.warn('[BagModal] Old schema detected! Triggering re-migration...');
        await triggerReMigration();
        return;
      }
      currentWeight.value = bagData.currentWeight || 0;
      maxLoad.value = bagData.maxLoad || 20;
      const itemsObj = bagData.items || {};
      const itemsEntries = Object.entries(itemsObj);
      if (itemsEntries.length === 0) {
        items.value = [];
        itemOrder.value = [];
        selectedItem.value = null;
        loading.value = false;
        showMoveToStorageButton.value = false;
        if (currentWeight.value !== 0) {
          const gameActions = projectFunctions.httpsCallable('gameActions');
          await gameActions({ action: 'recalculateWeight', target: 'bag' });
        }
        return;
      }
      const currentIds = itemsEntries.map(([instanceId]) => instanceId);
      itemOrder.value = [
        ...itemOrder.value.filter(instanceId => currentIds.includes(instanceId)),
        ...currentIds.filter(instanceId => !itemOrder.value.includes(instanceId)),
      ];
      const orderMap = itemOrder.value.reduce((acc, instanceId, index) => {
        acc[instanceId] = index;
        return acc;
      }, {});
      let hasInvalid = false;
      const validItems = itemsEntries.map(([instanceId, itemData]) => {
        if (!itemData || !itemData.templateId) {
          hasInvalid = true;
          return null;
        }
        const template = gameItems[itemData.templateId] || {};
        return {
          instanceId,
          id: instanceId,
          orderIndex: orderMap[instanceId] ?? Number.MAX_SAFE_INTEGER,
          templateId: itemData.templateId,
          states: itemData.states || {},
          locked: itemData.states?.locked || false,
          level: itemData.states?.level || null,
          ...template,
          ...(itemData.states || {}),
          name: itemData.states?.name || template.name || itemData.templateId,
          type: itemData.states?.type || template.type || '',
          rarity: itemData.states?.rarity ?? template.rarity ?? 1,
          rarityColors: itemData.states?.rarityColors ?? template.rarityColors ?? { bgColor: 'bg-gray-700', textColor: 'text-gray-500', borderColor: 'border-gray-600' },
          transfer: itemData.states?.transfer ?? template.transfer ?? false,
          decomposable: itemData.states?.decomposable ?? template.decomposable ?? false,
          upgradeCount: itemData.states?.upgradeCount ?? 0,
          statPoints: itemData.states?.statPoints ?? 0,
          upgradeStats: itemData.states?.upgradeStats ?? {},
          cardSlots: itemData.states?.cardSlots ?? [],
          cardStats: itemData.states?.cardStats ?? {},
          cardIds: itemData.states?.cardIds ?? (itemData.states?.cardSlots || []).map(slot => slot.cardInstanceId || slot.cardTemplateId),
          cardNames: itemData.states?.cardNames ?? (itemData.states?.cardSlots || []).map(slot => slot.cardName || slot.cardTemplateId),
          fuelLevel: itemData.states?.fuelLevel ?? template.fuelLevel ?? 0,
          maxFuelLevel: itemData.states?.maxFuelLevel ?? template.maxFuelLevel ?? 100,
        };
      }).filter(item => item !== null);
      items.value = validItems;
      const calculatedWeight = validItems.reduce((acc, item) => acc + (item.weight || 0), 0);
      if (calculatedWeight !== currentWeight.value || hasInvalid) {
        const gameActions = projectFunctions.httpsCallable('gameActions');
        await gameActions({ action: 'recalculateWeight', target: 'bag' });
      }
      loading.value = false;
      showMoveToStorageButton.value = items.value.length > 0;
    };

    const TRANSFERABLE_CARDS = [
          'การ์ดอธิการบดีวิญญาณ',
          'การ์ดผีอาจารย์นาฬิกา',
          'การ์ดอเวจีใต้ดิน',
          'การ์ดท่านหญิงกุหลาบโลหิต',
          'การ์ดราชันต์ลูกกอล์ฟนรก',
          'การ์ดโค้ชผีเหล็ก',
          'การ์ดจ้าวพายุแฟร์เวย์',
          'การ์ดครูพละร่างยักษ์ปีศาจสนาม',
          'การ์ดแม่ชีห้องสมุดเงา',
          'การ์ดคุณหญิงมรณะ',
          'การ์ดเจ้าพ่อเพชรดำ',
          'การ์ดจอมขมังเวทย์อสูรพันปี',
          'การ์ดนางศพพันธนาการ',
          'การ์ดผีตาไฟพันธุ์อาคม',
          'การ์ดพระยากายทิพย์',
          'การ์ดเจ้าพ่อสุสานดำ',
          'การ์ดเศียรเพลิงพันร่าง',
          'การ์ดแม่หมอเลือดจันทร์',
          'การ์ดเจ้ากระจกคำสาป',
          'การ์ดราชินีม้าหมุนเลือด',
          'การ์ดหัวหน้าเครื่องจักรโลหิต',
          'การ์ดผู้เผาเหล็กนิรันดร์',
          'การ์ดนักเชื่อมวิญญาณ',
          'การ์ดแม่มดศัลยกรรม',
          'การ์ดราชินีแห่งรอยยิ้มกระจก',
          'การ์ดภัณฑารักษ์เงา',
          'การ์ดราชินีดับเพลิง',
          'การ์ดแม่ชีเพลิงศักดิ์สิทธิ์',
          'การ์ดผู้สวดในเปลวไฟ',
          'การ์ดแม่ทัพกระดูกเพลิง',
          'การ์ดสิบเอกเหล็กกล้า',
          'การ์ดผู้พันโลหิต',
          'การ์ดราชินีผู้โดยสารสาบสูญ',
          'การ์ดคนไข้ทดลองล้มเหลว',
          'การ์ดหัวหน้าห้องดับจิต',
        ];

    const fixCardTransferIfNeeded = async (bagData) => {
      if (!bagData?.items) return;
      const itemsObj = bagData.items;
      const hasTransferableCard = Object.values(itemsObj).some(item => {
        const templateId = item.templateId || item.name;
        return TRANSFERABLE_CARDS.includes(templateId);
      });
      if (!hasTransferableCard) return;
      try {
        const gameActions = projectFunctions.httpsCallable('gameActions');
        const result = await gameActions({ action: 'fixCardTransfer' });
        if (result.data?.fixed > 0) {
          console.log(`[fixCardTransfer] ปรับ transfer ให้ ${result.data.fixed} ใบ`);
        }
      } catch (err) {
        console.error('fixCardTransfer error:', err);
      }
    };

    watch(() => inventoryStore.bagData, async (data) => {
      await processBagData(data);
      await fixCardTransferIfNeeded(data);
    }, { immediate: true });

    watch(() => playerStore.userData, (data) => {
      cash.value = data?.cash || 0;
    }, { immediate: true });

    watch(() => playerStore.characterPrivateData?.event, (eventId) => {
      if (eventUnsubscribe) {
        eventUnsubscribe();
        eventUnsubscribe = null;
      }
      if (eventId) {
        eventUnsubscribe = projectFirestore.collection('events').doc(eventId).onSnapshot(
          (doc) => {
            eventData.value = doc.exists ? doc.data() : null;
          },
          (err) => {
            console.error('Error fetching event data in BagModal:', err);
            eventData.value = null;
          }
        );
      } else {
        eventData.value = null;
      }
    }, { immediate: true });

    watch(() => isRestrictedInventoryEvent.value, (restricted) => {
      if (restricted) {
        warningMessage.value = 'ตัวละครกำลังอยู่ในเหตุการณ์พิเศษ ไม่สามารถจัดการกระเป๋าได้';
      } else if (warningMessage.value === 'ตัวละครกำลังอยู่ในเหตุการณ์พิเศษ ไม่สามารถจัดการกระเป๋าได้') {
        warningMessage.value = '';
      }
    }, { immediate: true });

    const setupBannedItems = () => {
      bannedUnsubscribe = projectFirestore.collection('server').doc('bannedSaleItems').onSnapshot((doc) => {
        if (doc.exists) {
          bannedItems.value = doc.data().bannedItems || [];
        } else {
          bannedItems.value = [];
        }
      }, (err) => {
        console.error('Error fetching banned items:', err);
      });
    };
    const resetActionStates = () => {
      confirmDiscard.value = false;
      confirmEquip.value = false;
      confirmLearn.value = false;
      confirmConsume.value = false;
      confirmReadMap.value = false;
      confirmTransfer.value = false;
      confirmDecompose.value = false;
      confirmHeal.value = false;
      confirmSmoke.value = false;
      confirmMoveToStorage.value = false;
      confirmUseVehicle.value = false;
      confirmLock.value = false;
      confirmUnlock.value = false;
      confirmInstallMechanical.value = false;
      confirmSummon.value = false;
      warningMessage.value = '';
      error.value = '';
    };
    const setAction = (action) => {
      resetActionStates();
      if (action) {
        switch (action) {
          case 'confirmDiscard':
            confirmDiscard.value = true;
            break;
          case 'confirmEquip':
            confirmEquip.value = true;
            break;
          case 'confirmLearn':
            confirmLearn.value = true;
            break;
          case 'confirmConsume':
            confirmConsume.value = true;
            break;
          case 'confirmReadMap':
            confirmReadMap.value = true;
            break;
          case 'confirmTransfer':
            confirmTransfer.value = true;
            break;
          case 'confirmDecompose':
            confirmDecompose.value = true;
            break;
          case 'confirmHeal':
            confirmHeal.value = true;
            break;
          case 'confirmSmoke':
            confirmSmoke.value = true;
            break;
          case 'confirmMoveToStorage':
            confirmMoveToStorage.value = true;
            break;
          case 'confirmUseVehicle':
            confirmUseVehicle.value = true;
            break;
          case 'confirmLock':
            confirmLock.value = true;
            break;
          case 'confirmUnlock':
            confirmUnlock.value = true;
            break;
          case 'confirmInstallMechanical':
            confirmInstallMechanical.value = true;
            break;
          case 'confirmSummon':
            confirmSummon.value = true;
            break;
        }
      }
    };
    const handleItemsMoved = () => {
      items.value = [];
      selectedItem.value = null;
      showMoveToStorageButton.value = false;
      showMoveToStorageModal.value = false;
      warningMessage.value = 'ย้ายไอเทมไปยังคลังสำเร็จ!';

    };
    const handleItemsDecomposed = () => {
      items.value = [];
      selectedItem.value = null;
      showDecomposeModal.value = false;
      warningMessage.value = 'แยกชิ้นส่วนไอเทมสำเร็จ!';

    };
    const handleItemsToggled = () => {
      items.value = [];
      selectedItem.value = null;
      showToggleLockModal.value = false;
      warningMessage.value = 'ปรับสถานะล็อกไอเทมสำเร็จ!';

    };
    const handleItemsSold = () => {
      items.value = [];
      selectedItem.value = null;
      showSellModal.value = false;
      warningMessage.value = 'ขายไอเทมสำเร็จ!';

    };
    const handleRobotUpdated = (updatedItem) => {
      const index = items.value.findIndex(i => i.id === updatedItem.id);
      if (index !== -1) {
        items.value[index] = { ...items.value[index], ...updatedItem };
        warningMessage.value = 'อัปเกรดหุ่นยนต์สำเร็จ!';
      }
      showKillerRobotModal.value = false;
    };
    const handleVehicleUpdated = (updatedItem) => {
  const index = items.value.findIndex(i => i.id === updatedItem.id || i.instanceId === updatedItem.instanceId)

  if (index !== -1) {
    const oldItem = items.value[index]
    const nextFuelLevel = updatedItem.states?.fuelLevel ?? updatedItem.fuelLevel ?? oldItem.states?.fuelLevel ?? oldItem.fuelLevel ?? 0
    const nextMaxFuelLevel = updatedItem.states?.maxFuelLevel ?? updatedItem.maxFuelLevel ?? oldItem.states?.maxFuelLevel ?? oldItem.maxFuelLevel ?? 100

    const nextItem = {
      ...oldItem,
      ...updatedItem,
      fuelLevel: nextFuelLevel,
      maxFuelLevel: nextMaxFuelLevel,
      states: {
        ...(oldItem.states || {}),
        ...(updatedItem.states || {}),
        fuelLevel: nextFuelLevel,
        maxFuelLevel: nextMaxFuelLevel
      }
    }

    items.value[index] = nextItem

    if (selectedItem.value && (selectedItem.value.id === nextItem.id || selectedItem.value.instanceId === nextItem.instanceId)) {
      selectedItem.value = nextItem
    }

    warningMessage.value = 'เติมน้ำมันสำเร็จ!'
  }

  showVehicleFuelModal.value = false
}
    const handleMechanicalUpdated = (updatedItem) => {
      const index = items.value.findIndex(i => i.id === updatedItem.id);
      if (index !== -1) {
        items.value[index] = { ...items.value[index], ...updatedItem, ...(updatedItem.states || {}) };
        warningMessage.value = 'อัปเกรดอวัยวะจักรกลสำเร็จ!';
      }
      showMechanicalUpgradeModal.value = false;
    };
    const handleEquipmentUpdated = (updatedItem) => {
      const index = items.value.findIndex(i => i.id === updatedItem.id);
      if (index !== -1) {
        items.value[index] = { ...items.value[index], ...updatedItem, ...(updatedItem.states || {}) };
        warningMessage.value = 'อัปเกรดไอเทมสำเร็จ!';
      }
      showEquipmentUpgradeModal.value = false;
    };
    const handleCardInstalled = (updatedItem) => {
      const index = items.value.findIndex(i => i.id === updatedItem.id);
      if (index !== -1) {
        items.value[index] = { ...items.value[index], ...updatedItem, ...(updatedItem.states || {}) };
        warningMessage.value = 'ติดตั้งการ์ดสำเร็จ!';
      }
      const selectedCard = selectedItem.value;
      if (selectedCard?.id) {
        items.value = items.value.filter(item => item.id !== selectedCard.id);
        itemOrder.value = itemOrder.value.filter(id => id !== selectedCard.id);
      }
      selectedItem.value = null;
      showInstallCardModal.value = false;
      resetActionStates();
    };
    const handleCardUninstalled = (updatedItem) => {
      const index = items.value.findIndex(i => i.id === updatedItem.id);
      if (index !== -1) {
        items.value[index] = { ...items.value[index], ...updatedItem, ...(updatedItem.states || {}) };
        warningMessage.value = 'ถอนการติดตั้งการ์ดสำเร็จ!';
      }
      showUninstallCardModal.value = false;
    };
    const handleBombSuccess = async (itemId) => {
      warningMessage.value = 'ติดตั้งระเบิดสำเร็จ!';
      showInstallBombModal.value = false;
      await nextTick();
      items.value = items.value.filter(item => item.id !== itemId);
      selectedItem.value = null;
    };
    const handleDisarmSuccess = (itemId) => {
      warningMessage.value = 'ปลดกัปดักสำเร็จ!';
      showDisarmTrapModal.value = false;
      items.value = items.value.filter(item => item.id !== itemId);
      selectedItem.value = null;
    };
    const handleLootSuccess = async (itemId) => {
  warningMessage.value = 'กำลังดักรอปล้น!';
  showLootModal.value = false;
  selectedItem.value = null;  // ← ย้ายมาก่อน nextTick
  await nextTick();
  if (itemId) {
    items.value = items.value.filter(item => item.id !== itemId);
  }
  emit('closeInventory');
};
    const handleExorciseSuccess = async (itemId) => {
      warningMessage.value = 'ไล่ผีสำเร็จ!';
      showExorciseModal.value = false;
      await nextTick();
      items.value = items.value.filter(item => item.id !== itemId);
      selectedItem.value = null;
    };
    const selectItem = (item) => {
      selectedItem.value = item;
      resetActionStates();
    };
    const openDetailModal = (item) => {
      selectedItem.value = item;
      showDetailModal.value = true;
    };
    const toggleActionsDropdown = () => {
      showActionsDropdown.value = !showActionsDropdown.value;
    };
    const toggleLock = async (newLocked) => {
      if (!selectedItem.value) return;
      isLoading.value = true;
      try {
        const gameActions = projectFunctions.httpsCallable('gameActions');
        await gameActions({ action: 'toggleItemLock', instanceId: selectedItem.value.instanceId });
        warningMessage.value = newLocked ? 'ล็อกไอเทมสำเร็จ!' : 'ปลดล็อกไอเทมสำเร็จ!';
        if (selectedItem.value.states) selectedItem.value.states.locked = newLocked;
        selectedItem.value.locked = newLocked;
        resetActionStates();
      } catch (err) {
        error.value = err.message;
      } finally {
        isLoading.value = false;
      }
    };
    const useVehicle = async () => {
      if (!selectedItem.value) return;
      isLoading.value = true;
      try {
        const gameActions = projectFunctions.httpsCallable('gameActions');
        const result = await gameActions({ action: 'vehicleAction', subAction: 'use', instanceId: selectedItem.value.instanceId });
        currentWeight.value = result.data.newWeight;
        warningMessage.value = `ใช้งาน${selectedItem.value.name}สำเร็จ!`;
        selectedItem.value = null;
        resetActionStates();
      } catch (err) {
        error.value = err.message;
      } finally {
        isLoading.value = false;
      }
    };
    const useRobot = () => {
      showKillerRobotModal.value = true;
    };
    const readMap = async () => {
      if (!selectedItem.value) return;
      isLoading.value = true;
      try {
        const gameActions = projectFunctions.httpsCallable('gameActions');
        await gameActions({ action: 'readMap2', instanceId: selectedItem.value.instanceId });
        warningMessage.value = 'อ่านแผนที่สำเร็จ!';
        selectedItem.value = null;
        resetActionStates();
      } catch (err) {
        error.value = err.message;
      } finally {
        isLoading.value = false;
      }
    };
    const consumeFood = async () => {
      if (!selectedItem.value) return;
      isLoading.value = true;
      try {
        const gameActions = projectFunctions.httpsCallable('gameActions');
        await gameActions({ action: 'consumeFood', instanceId: selectedItem.value.instanceId });
        warningMessage.value = 'กินอาหารสำเร็จ!';
        selectedItem.value = null;
        resetActionStates();
      } catch (err) {
        error.value = err.message;
      } finally {
        isLoading.value = false;
      }
    };
    const smokeCigarette = async () => {
      if (!selectedItem.value) return;
      isLoading.value = true;
      try {
        const gameActions = projectFunctions.httpsCallable('gameActions');
        await gameActions({ action: 'consumeFood', instanceId: selectedItem.value.instanceId });
        warningMessage.value = 'ดูดบุหรี่สำเร็จ!';
        selectedItem.value = null;
        resetActionStates();
      } catch (err) {
        error.value = err.message;
      } finally {
        isLoading.value = false;
      }
    };
    const learnSkill = async () => {
      if (!selectedItem.value) return;
      isLoading.value = true;
      try {
        const gameActions = projectFunctions.httpsCallable('gameActions');
        await gameActions({ action: 'learnSkill', instanceId: selectedItem.value.instanceId });
        warningMessage.value = 'เรียนรู้ทักษะสำเร็จ!';
        selectedItem.value = null;
        resetActionStates();
      } catch (err) {
        error.value = err.message;
      } finally {
        isLoading.value = false;
      }
    };
    const equipItem = async () => {
      if (!selectedItem.value) return;
      isLoading.value = true;
      error.value = '';
      warningMessage.value = '';
      try {
        const gameActions = projectFunctions.httpsCallable('gameActions');
        await gameActions({ action: 'equipItem', instanceId: selectedItem.value.instanceId });
        resetActionStates();
        warningMessage.value = 'ใส่ไอเทมสำเร็จ!';
      } catch (err) {
        error.value = err.message;
      } finally {
        isLoading.value = false;
      }
    };
    const installMechanicalOrgan = async () => {
      if (!selectedItem.value) return;
      isLoading.value = true;
      try {
        const gameActions = projectFunctions.httpsCallable('gameActions');
        await gameActions({ action: 'mechanicalOrganAction', subAction: 'install', instanceId: selectedItem.value.instanceId });
        warningMessage.value = 'ติดตั้งอวัยวะจักรกลสำเร็จ!';
        selectedItem.value = null;
        resetActionStates();
      } catch (err) {
        error.value = err.message;
      } finally {
        isLoading.value = false;
      }
    };
    const summonGhost = async () => {
      if (!selectedItem.value || !selectedItem.value.id) {
        error.value = 'ไม่สามารถอัญเชิญได้: ไม่พบไอเทม';
        return;
      }
      isLoading.value = true;
      const itemId = selectedItem.value.id; // Store itemId before setting selectedItem to null
      try {
        const gameActions = projectFunctions.httpsCallable('gameActions');
        const result = await gameActions({ action: 'ghostCommandAction', subAction: 'summon', instanceId: itemId });
        warningMessage.value = result.data.message || 'อัญเชิญวิญญาณสำเร็จ!';
        await nextTick();
        items.value = items.value.filter(item => item.id !== itemId); // Use stored itemId
        selectedItem.value = null;
        resetActionStates();
      } catch (err) {
        error.value = err.message || 'เกิดข้อผิดพลาดในการอัญเชิญวิญญาณ';
      } finally {
        isLoading.value = false;
      }
    };
    const transferItem = async () => {
      if (!selectedItem.value || !recipientUid.value) {
        error.value = 'กรุณากรอก UID ผู้รับ';
        return;
      }
      isLoading.value = true;
      try {
        const gameActions = projectFunctions.httpsCallable('gameActions');
        await gameActions({ action: 'transferItem', instanceId: selectedItem.value.instanceId, recipientUid: recipientUid.value });
        warningMessage.value = 'ส่งไอเทมสำเร็จ!';
        selectedItem.value = null;
        recipientUid.value = '';
        resetActionStates();
      } catch (err) {
        error.value = err.message;
      } finally {
        isLoading.value = false;
      }
    };
    const decomposeItem = async () => {
      if (!selectedItem.value) return;
      if (selectedItem.value.name === 'กองขยะ') {
        error.value = 'ไม่สามารถแยกชิ้นส่วนกองขยะได้';
        return;
      }
      isLoading.value = true;
      try {
        const gameActions = projectFunctions.httpsCallable('gameActions');
        await gameActions({ action: 'decomposeItem', instanceId: selectedItem.value.instanceId });
        warningMessage.value = 'แยกชิ้นส่วนสำเร็จ!';
        selectedItem.value = null;
        resetActionStates();
      } catch (err) {
        error.value = err.message;
      } finally {
        isLoading.value = false;
      }
    };
    const heal = async () => {
      if (!selectedItem.value) return;
      isLoading.value = true;
      try {
        const gameActions = projectFunctions.httpsCallable('gameActions');
        await gameActions({ action: 'heal', instanceId: selectedItem.value.instanceId });
        warningMessage.value = 'รักษาสำเร็จ!';
        selectedItem.value = null;
        resetActionStates();
      } catch (err) {
        error.value = err.message;
      } finally {
        isLoading.value = false;
      }
    };
    const useTrap = async () => {
      if (!selectedItem.value) return;
      isLoading.value = true;
      try {
        const gameActions = projectFunctions.httpsCallable('gameActions');
        await gameActions({ action: 'useTrap', instanceId: selectedItem.value.instanceId });
        warningMessage.value = 'ใช้งานกัปดักสำเร็จ!';
        selectedItem.value = null;
        emit('closeInventory');
      } catch (err) {
        error.value = err.message;
      } finally {
        isLoading.value = false;
      }
    };
    const moveToStorage = async () => {
      if (!selectedItem.value) return;
      isLoading.value = true;
      try {
        const gameActions = projectFunctions.httpsCallable('gameActions');
        await gameActions({ action: 'moveItemToStorage', instanceId: selectedItem.value.instanceId });
        warningMessage.value = 'เก็บเข้าคลังสำเร็จ!';
        items.value = items.value.filter(item => item.instanceId !== selectedItem.value.instanceId);
        selectedItem.value = null;
        resetActionStates();
        showMoveToStorageButton.value = items.value.length > 0;
      } catch (err) {
        error.value = err.message;
      } finally {
        isLoading.value = false;
      }
    };
    const discardItem = async () => {
      if (!selectedItem.value) return;
      if (selectedItem.value.states?.locked) {
        error.value = 'ไม่สามารถทิ้งไอเทมที่ถูกล็อกได้';
        return;
      }
      isLoading.value = true;
      try {
        const gameActions = projectFunctions.httpsCallable('gameActions');
        const discardedId = selectedItem.value.instanceId;
        await gameActions({ action: 'discardItem', instanceId: discardedId });
        warningMessage.value = 'ทิ้งไอเทมสำเร็จ!';
        items.value = items.value.filter(item => item.instanceId !== discardedId);
        itemOrder.value = itemOrder.value.filter(instanceId => instanceId !== discardedId);
        selectedItem.value = null;
        resetActionStates();
      } catch (err) {
        error.value = err.message;
      } finally {
        isLoading.value = false;
      }
    };
    const upgradeBag = async () => {
      if (maxLoad.value >= 200 || cash.value < nextUpgradeCost.value) {
        error.value = 'ไม่สามารถอัปเกรดได้';
        return;
      }
      isUpgrading.value = true;
      try {
        const gameActions = projectFunctions.httpsCallable('gameActions');
        await gameActions({ action: 'upgradeBag' });
        warningMessage.value = 'อัปเกรดกระเป๋าสำเร็จ!';
        showBagUpgradeModal.value = false;
      } catch (err) {
        error.value = err.message;
      } finally {
        isUpgrading.value = false;
      }
    };
    onMounted(() => {
      loadDisplayPreference();
      loadSortOrder();
      setupBannedItems();
      document.addEventListener('click', handleClickOutside);
    });
    onUnmounted(() => {
      if (bannedUnsubscribe) bannedUnsubscribe();
      if (eventUnsubscribe) eventUnsubscribe();
      document.removeEventListener('click', handleClickOutside);
    });
    const handleClickOutside = (event) => {
      if (!event.target.closest('.relative') && showActionsDropdown.value) {
        showActionsDropdown.value = false;
      }
    };
    return {
      items,
      currentWeight,
      maxLoad,
      cash,
      loading,
      error,
      warningMessage,
      selectedItem,
      confirmDiscard,
      confirmEquip,
      confirmLearn,
      confirmConsume,
      confirmReadMap,
      confirmTransfer,
      confirmDecompose,
      confirmHeal,
      confirmSmoke,
      confirmMoveToStorage,
      confirmUseVehicle,
      confirmLock,
      confirmUnlock,
      confirmInstallMechanical,
      confirmSummon,
      showBagUpgradeModal,
      showMoveToStorageModal,
      showDecomposeModal,
      showToggleLockModal,
      toggleLockMode,
      showSellModal,
      showKillerRobotModal,
      showVehicleFuelModal,
      showMechanicalUpgradeModal,
      showEquipmentUpgradeModal,
      showUseTrapModal,
      showDisarmTrapModal,
      showInstallBombModal,
      showLootModal,
      showInstallCardModal,
      showUninstallCardModal,
      showExorciseModal,
      showActionsDropdown,
      showMoveToStorageButton,
      isUpgrading,
      recipientUid,
      isLoading,
      showDetailModal,
      defaultImg,
      displayPreference,
      gameItems,
      bannedItems,
      nextUpgradeCost,
      hasFullHP,
      isRestrictedInventoryEvent,
      hasDecomposableItems,
      hasLockableItems,
      hasLockedItems,
      hasSellableItems,
      unlockedItems,
      unlockedDecomposableItems,
      sortedItemsComputed,
      bookTypeOrder,
      updatePreferences,
      handleImageError,
      handleItemsMoved,
      handleItemsDecomposed,
      handleItemsToggled,
      handleItemsSold,
      handleRobotUpdated,
      handleVehicleUpdated,
      handleMechanicalUpdated,
      handleEquipmentUpdated,
      handleCardInstalled,
      handleCardUninstalled,
      handleBombSuccess,
      handleDisarmSuccess,
      handleLootSuccess,
      handleExorciseSuccess,
      selectItem,
      openDetailModal,
      toggleActionsDropdown,
      toggleLock,
      useVehicle,
      useRobot,
      readMap,
      consumeFood,
      smokeCigarette,
      learnSkill,
      equipItem,
      installMechanicalOrgan,
      summonGhost,
      transferItem,
      decomposeItem,
      heal,
      useTrap,
      moveToStorage,
      discardItem,
      upgradeBag,
      isUpgradableEquipment,
      isEquippable,
      isKillerRobotItem,
      isInstallableCard,
      hasInstalledCards,
      setAction,
    };
  },
};
</script>
<style scoped>
.scrollbar-thin::-webkit-scrollbar {
  width: 5px;
}
.scrollbar-thin::-webkit-scrollbar-track {
  background: #2d3748;
  border-radius: 10px;
}
.scrollbar-thin::-webkit-scrollbar-thumb {
  background: #4a5568;
  border-radius: 10px;
}
.scrollbar-thin::-webkit-scrollbar-thumb:hover {
  background: #6b7280;
}
.scrollbar-thin {
  scrollbar-width: thin;
  scrollbar-color: #4a5568 #2d3748;
}
.animate-spin {
  animation: spin 1s linear infinite;
}
.animate-spin-pulse {
  animation: spin-pulse 1.2s ease-in-out infinite;
}
@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}
@keyframes spin-pulse {
  0% { transform: rotate(0deg) scale(1); }
  50% { transform: rotate(180deg) scale(1.2); }
  100% { transform: rotate(360deg) scale(1); }
}
@keyframes tabSlide {
  from { width: 0; }
  to { width: 100%; }
}
.animate-tabSlide {
  animation: tabSlide 0.3s ease-out;
}
@keyframes modalFade {
  from { opacity: 0; }
  to { opacity: 1; }
}
.animate-modalFade {
  animation: modalFade 0.3s ease-out;
}
@keyframes scaleIn {
  from { transform: scale(0.95); opacity: 0; }
  to { transform: scale(1); opacity: 1; }
}
.animate-scaleIn {
  animation: scaleIn 0.3s ease-out;
}
@keyframes swing {
  0% { transform: rotate(0deg); }
  25% { transform: rotate(15deg); }
  50% { transform: rotate(0deg); }
  75% { transform: rotate(-15deg); }
  100% { transform: rotate(0deg); }
}
.animate-swing {
  animation: swing 2s ease-in-out infinite;
}
button:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}
button:not(:disabled):active {
  transform: scale(0.98);
}
input:focus {
  outline: none;
  box-shadow: 0 0 0 2px #6366f1;
}
.item-enter-active,
.item-leave-active {
  transition: all 0.3s ease;
}
.item-enter-from,
.item-leave-to {
  opacity: 0;
  transform: translateY(10px);
}
.dropdown-enter-active,
.dropdown-leave-active {
  transition: all 0.2s ease;
}
.dropdown-enter-from,
.dropdown-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}
</style>
